import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface MatchFormat {
  id: string;
  name: string;
  maxSets: number;
  setsToWin: number;
  setFormat: string; // GAMES или POINTS
  gamesToWin: number;
  tiebreakAt: number;
  pointsToWin: number;
  noAd: boolean;
  hasTiebreak: boolean;
  tiebreakTo: number;
  superTiebreak: boolean;
  superTiebreakTo: number;
  description?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

const MatchFormats: React.FC = () => {
  const [formats, setFormats] = useState<MatchFormat[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingFormat, setEditingFormat] = useState<MatchFormat | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    maxSets: 3,
    setsToWin: 2,
    setFormat: 'GAMES',
    gamesToWin: 6,
    tiebreakAt: 6,
    pointsToWin: 4,
    noAd: false,
    hasTiebreak: true,
    tiebreakTo: 7,
    superTiebreak: false,
    superTiebreakTo: 10,
    description: '',
    isDefault: false
  });

  useEffect(() => {
    fetchFormats();
  }, []);

  const fetchFormats = async () => {
    try {
      setLoading(true);
      const data = await apiGet<MatchFormat[]>('/match-formats');
      setFormats(data);
    } catch (error) {
      console.error('Ошибка загрузки форматов:', error);
      setFormats([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFormat(null);
    setFormData({
      name: '',
      maxSets: 3,
      setsToWin: 2,
      setFormat: 'GAMES',
      gamesToWin: 6,
      tiebreakAt: 6,
      pointsToWin: 4,
      noAd: false,
      hasTiebreak: true,
      tiebreakTo: 7,
      superTiebreak: false,
      superTiebreakTo: 10,
      description: '',
      isDefault: false
    });
    setIsModalOpen(true);
  };

  const handleEdit = (format: MatchFormat) => {
    setEditingFormat(format);
    setFormData({
      name: format.name,
      maxSets: format.maxSets,
      setsToWin: format.setsToWin,
      setFormat: format.setFormat || 'GAMES',
      gamesToWin: format.gamesToWin || 6,
      tiebreakAt: format.tiebreakAt || 6,
      pointsToWin: format.pointsToWin || 4,
      noAd: format.noAd || false,
      hasTiebreak: format.hasTiebreak,
      tiebreakTo: format.tiebreakTo,
      superTiebreak: format.superTiebreak,
      superTiebreakTo: format.superTiebreakTo,
      description: format.description || '',
      isDefault: format.isDefault
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingFormat) {
        await apiPut(`/match-formats/${editingFormat.id}`, formData);
      } else {
        await apiPost('/match-formats', formData);
      }
      setIsModalOpen(false);
      await fetchFormats();
    } catch (error: any) {
      alert(`Ошибка сохранения: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот формат?')) return;
    
    try {
      await apiDelete(`/match-formats/${id}`);
      await fetchFormats();
    } catch (error: any) {
      alert(`Ошибка удаления: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleMaxSetsChange = (value: number) => {
    setFormData({
      ...formData,
      maxSets: value,
      setsToWin: Math.floor(value / 2) + 1 // Автоматически рассчитываем setsToWin
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Форматы счета матчей</h1>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Создать формат
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Загрузка форматов...</p>
        </div>
      ) : formats.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>Форматы не найдены</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Макс. сетов
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Сетов для победы
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тайбрейк
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Супер-тайбрейк
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  По умолчанию
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {formats.map((format) => (
                <tr key={format.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {format.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format.maxSets}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format.setsToWin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format.hasTiebreak ? `Да (до ${format.tiebreakTo})` : 'Нет'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format.superTiebreak ? `Да (до ${format.superTiebreakTo})` : 'Нет'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {format.isDefault && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                        Да
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(format)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Изменить
                    </button>
                    <button
                      onClick={() => handleDelete(format.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Модальное окно */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingFormat ? 'Редактировать формат' : 'Создать формат'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Например: До 2 сетов"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Максимальное количество сетов *
                </label>
                <select
                  value={formData.maxSets}
                  onChange={(e) => handleMaxSetsChange(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value={1}>1 сет</option>
                  <option value={2}>2 сета</option>
                  <option value={3}>3 сета</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Сетов для победы *
                </label>
                <input
                  type="number"
                  min="1"
                  max={formData.maxSets}
                  value={formData.setsToWin}
                  onChange={(e) => setFormData({ ...formData, setsToWin: parseInt(e.target.value) || 1 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Обычно: {Math.floor(formData.maxSets / 2) + 1} для победы из {formData.maxSets} сетов
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Формат полных сетов *
                </label>
                <select
                  value={formData.setFormat}
                  onChange={(e) => setFormData({ ...formData, setFormat: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="GAMES">Геймы</option>
                  <option value="POINTS">Очки</option>
                </select>
              </div>

              {formData.setFormat === 'GAMES' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      До скольки геймов для победы в сете *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.gamesToWin}
                      onChange={(e) => setFormData({ ...formData, gamesToWin: parseInt(e.target.value) || 6 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Обычно: 6 геймов
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      При каком счете тайбрейк *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.tiebreakAt}
                        onChange={(e) => setFormData({ ...formData, tiebreakAt: parseInt(e.target.value) || 6 })}
                        className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-center"
                      />
                      <span className="text-sm text-gray-600">-</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.tiebreakAt}
                        disabled
                        className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-center bg-gray-100 text-gray-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Тайбрейк играется при счете {formData.tiebreakAt}-{formData.tiebreakAt} геймов
                    </p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="noAd"
                      checked={formData.noAd}
                      onChange={(e) => setFormData({ ...formData, noAd: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="noAd" className="text-sm font-medium text-gray-700">
                      No-ad при 40:40 (решающий мяч)
                    </label>
                  </div>
                </>
              )}

              {formData.setFormat === 'POINTS' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    До скольки очков для победы в сете *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.pointsToWin}
                    onChange={(e) => setFormData({ ...formData, pointsToWin: parseInt(e.target.value) || 4 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Обычно: 4 очка (15, 30, 40, game)
                  </p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasTiebreak"
                  checked={formData.hasTiebreak}
                  onChange={(e) => setFormData({ ...formData, hasTiebreak: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="hasTiebreak" className="text-sm font-medium text-gray-700">
                  Тайбрейк в сете (при 6-6)
                </label>
              </div>

              {formData.hasTiebreak && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    До скольки очков в тайбрейке
                  </label>
                  <select
                    value={formData.tiebreakTo}
                    onChange={(e) => setFormData({ ...formData, tiebreakTo: parseInt(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value={7}>7 очков (обычный тайбрейк)</option>
                    <option value={10}>10 очков (супер-тайбрейк в сете)</option>
                  </select>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="superTiebreak"
                  checked={formData.superTiebreak}
                  onChange={(e) => setFormData({ ...formData, superTiebreak: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="superTiebreak" className="text-sm font-medium text-gray-700">
                  Супер-тайбрейк вместо 3-го сета
                </label>
              </div>

              {formData.superTiebreak && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    До скольки очков в супер-тайбрейке
                  </label>
                  <select
                    value={formData.superTiebreakTo}
                    onChange={(e) => setFormData({ ...formData, superTiebreakTo: parseInt(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value={10}>10 очков</option>
                    <option value={7}>7 очков</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Дополнительное описание формата..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isDefault" className="text-sm font-medium text-gray-700">
                  Использовать по умолчанию
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSave}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
              >
                Сохранить
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchFormats;

